// src/pages/PoliticaPrivacidade.jsx
import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Paper,
  Avatar,
  Button,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

export default function PoliticaPrivacidade() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: theme.palette.primary.main,
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    surfaceStrong: isDark ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)",
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const sections = [
    {
      title: "Responsável pelo tratamento",
      text: "A Sintanet é responsável pelo tratamento dos dados pessoais recolhidos através desta aplicação.",
    },
    {
      title: "Dados que recolhemos",
      text: "Podemos recolher dados de identificação, contacto, faturação, autenticação, utilização da app e dados necessários à prestação dos serviços.",
    },
    {
      title: "Finalidades do tratamento",
      text: "Tratamos os dados para gestão de contas, prestação do serviço, apoio ao cliente, faturação, cumprimento de obrigações legais, segurança e melhoria da plataforma.",
    },
    {
      title: "Fundamento jurídico",
      text: "O tratamento baseia-se, consoante o caso, na execução de contrato, cumprimento de obrigações legais, interesse legítimo e, quando aplicável, consentimento.",
    },
    {
      title: "Partilha de dados",
      text: "Os dados podem ser partilhados com subcontratantes e prestadores de serviços tecnológicos, incluindo alojamento, autenticação, analytics, faturação e suporte, apenas na medida necessária.",
    },
    {
      title: "Conservação",
      text: "Conservamos os dados apenas pelo período necessário às finalidades do tratamento e ao cumprimento de obrigações legais.",
    },
    {
      title: "Direitos dos titulares",
      text: "O utilizador pode solicitar acesso, retificação, apagamento, limitação, oposição e portabilidade dos dados, nos termos legais aplicáveis.",
    },
    {
      title: "Contacto",
      text: "Para questões de privacidade, contacta: privacidade@teudominio.pt",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        px: { xs: 1.5, sm: 2.5, md: 4 },
        py: { xs: 2.5, md: 5 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.2)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, rgba(20,184,166,0.12), transparent 58%), radial-gradient(760px 520px at 50% 100%, rgba(59,130,246,0.10), transparent 60%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, rgba(20,184,166,0.14), transparent 58%), radial-gradient(760px 520px at 50% 100%, rgba(59,130,246,0.10), transparent 60%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.14 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 78%)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 1120, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: { xs: "30px", md: "46px" },
            border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(255,255,255,0.82)"}`,
            bgcolor: colors.surface,
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            boxShadow: isDark
              ? "0 34px 110px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.06)"
              : "0 34px 110px rgba(15,23,42,0.14), inset 0 1px 0 rgba(255,255,255,0.85)",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: "34px",
              bgcolor: colors.hero,
              color: "common.white",
              boxShadow: isDark
                ? "0 24px 70px rgba(0,0,0,0.34)"
                : "0 24px 70px rgba(15,23,42,0.22)",
              "&:before": {
                content: '""',
                position: "absolute",
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.46)} 0%, ${alpha(colors.accent, 0.18)} 42%, transparent 70%)`,
              },
              "&:after": {
                content: '""',
                position: "absolute",
                width: 360,
                height: 360,
                left: "38%",
                bottom: -230,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.2)} 0%, transparent 68%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip
                    icon={<ShieldRoundedIcon />}
                    label="Proteção de dados"
                    size="small"
                    sx={{
                      height: 32,
                      borderRadius: "999px",
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      fontWeight: 850,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                  <Chip
                    icon={<VerifiedUserRoundedIcon />}
                    label="Privacidade"
                    size="small"
                    sx={{
                      height: 32,
                      borderRadius: "999px",
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      fontWeight: 850,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                </Stack>

                <Typography
                  sx={{
                    fontWeight: 950,
                    lineHeight: 0.98,
                    fontSize: { xs: "2.35rem", sm: "3rem", md: "4.05rem" },
                    maxWidth: 760,
                    letterSpacing: "-0.075em",
                    textShadow: "0 22px 54px rgba(0,0,0,0.24)",
                  }}
                >
                  Política de Privacidade
                </Typography>

                <Typography
                  sx={{
                    mt: 2.2,
                    maxWidth: 720,
                    color: "rgba(255,255,255,0.72)",
                    fontSize: { xs: "0.98rem", md: "1.08rem" },
                    lineHeight: 1.78,
                  }}
                >
                  Consulta como a Sintanet recolhe, utiliza, protege e conserva os teus dados pessoais.
                </Typography>
              </Box>

              <Avatar
                sx={{
                  width: { xs: 78, md: 104 },
                  height: { xs: 78, md: 104 },
                  borderRadius: { xs: "26px", md: "34px" },
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14), 0 20px 44px rgba(0,0,0,0.25)",
                  flexShrink: 0,
                }}
              >
                <PrivacyTipRoundedIcon sx={{ fontSize: { xs: 38, md: 52 } }} />
              </Avatar>
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2.2,
                borderRadius: "26px",
                display: "flex",
                alignItems: "center",
                gap: 1.4,
                bgcolor: colors.surfaceStrong,
                border: `1px solid ${colors.border}`,
                boxShadow: isDark
                  ? "0 18px 42px rgba(0,0,0,0.22)"
                  : "0 16px 38px rgba(15,23,42,0.06)",
              }}
            >
              <Avatar
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "17px",
                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                  color: colors.accent,
                }}
              >
                <UpdateRoundedIcon />
              </Avatar>

              <Box>
                <Typography sx={{ fontWeight: 950, color: colors.text }}>
                  Última atualização
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2026-05-05
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2.2,
                borderRadius: "26px",
                display: "flex",
                alignItems: "center",
                gap: 1.4,
                bgcolor: colors.surfaceStrong,
                border: `1px solid ${colors.border}`,
                boxShadow: isDark
                  ? "0 18px 42px rgba(0,0,0,0.22)"
                  : "0 16px 38px rgba(15,23,42,0.06)",
              }}
            >
              <Avatar
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "17px",
                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                  color: colors.accent,
                }}
              >
                <LockRoundedIcon />
              </Avatar>

              <Box>
                <Typography sx={{ fontWeight: 950, color: colors.text }}>
                  Dados pessoais
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tratamento e direitos
                </Typography>
              </Box>
            </Paper>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "34px",
              bgcolor: colors.surfaceStrong,
              border: `1px solid ${colors.border}`,
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.28)"
                : "0 24px 60px rgba(15,23,42,0.07)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
              <Stack direction="row" spacing={1.45} alignItems="center">
                <Avatar
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "17px",
                    bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                    color: colors.accent,
                  }}
                >
                  <ArticleRoundedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                    Tratamento de dados
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    Informação essencial sobre privacidade e direitos dos utilizadores.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: colors.border }} />

            <Stack spacing={1.35} sx={{ p: { xs: 2, md: 2.5 } }}>
              {sections.map((section, index) => (
                <Paper
                  key={section.title}
                  elevation={0}
                  sx={{
                    p: { xs: 2.2, md: 2.7 },
                    borderRadius: "24px",
                    bgcolor: isDark ? "rgba(2,6,23,0.34)" : "rgba(248,250,252,0.82)",
                    border: `1px solid ${colors.border}`,
                    transition: "all 0.22s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 18,
                      bottom: 18,
                      width: 4,
                      borderRadius: "999px",
                      background: `linear-gradient(180deg, ${colors.accent}, #2563eb, #0f766e)`,
                      opacity: 0.9,
                    },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isDark
                        ? "0 18px 44px rgba(0,0,0,0.28)"
                        : "0 18px 44px rgba(15,23,42,0.10)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "16px",
                        flexShrink: 0,
                        color: colors.accent,
                        bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                        border: `1px solid ${alpha(colors.accent, 0.14)}`,
                        fontWeight: 950,
                        fontSize: 14,
                      }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </Avatar>

                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 950,
                          mb: 0.8,
                          color: colors.text,
                          letterSpacing: "-0.025em",
                        }}
                      >
                        {section.title}
                      </Typography>

                      <Typography sx={{ color: colors.muted, lineHeight: 1.75, fontSize: "0.98rem" }}>
                        {section.text}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mt: 3 }}
          >
            <Button
              component={RouterLink}
              to="/login"
              startIcon={<ArrowBackRoundedIcon />}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 900,
                color: colors.text,
                bgcolor: colors.surfaceStrong,
                border: `1px solid ${colors.border}`,
                px: 2.2,
                "&:hover": { bgcolor: isDark ? "rgba(30,41,59,0.96)" : "#ffffff" },
              }}
            >
              Voltar ao login
            </Button>

            <Typography variant="caption" sx={{ textAlign: { xs: "center", sm: "right" }, color: colors.muted }}>
              Sintanet · Política de Privacidade
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
